'use client';

import { useQuery } from '@tanstack/react-query';

import { UnauthQueryOptions } from '@/api';
import { DesktopSideAdBanner } from '@/app/_components/ads';
import { DesktopPageShell } from '@/components/template/DesktopPageShell';

import {
  EMPTY_AI_SUMMARY_MESSAGE,
  PAGE_SIZE,
  PREVIEW_SIZE,
} from '../../constants';
import { formatApplicationPeriod } from '../../utils';
import type { PolicyData } from '../PolicySearch/SearchPolicyCard';
import { SearchEmptyResults } from '../SearchEmptyResults';
import { BestContentsPreviewSection } from './BestContentsPreviewSection';
import { DonationBusinessPreviewSection } from './DonationBusinessPreviewSection';
import { GiftPreviewSection } from './GiftPreviewSection';
import { HometownNewsPreviewSection } from './HometownNewsPreviewSection';
import { LocalMarketPreviewSection } from './LocalMarketPreviewSection';
import { PolicyPreviewSection } from './PolicyPreviewSection';

const SLIDER_PREVIEW_SIZE = PAGE_SIZE;
const DONATION_PREVIEW_SIZE = 3;

interface AllSearchProps {
  keyword: string;
}

export function AllSearch({ keyword }: AllSearchProps) {
  const policyQuery = useQuery({
    ...UnauthQueryOptions['get:/wello/v2/allows/query/policies']({
      query: {
        searchKeyword: keyword || undefined,
        policySort: 'POPULARITY',
        page: 1,
        size: PREVIEW_SIZE,
      },
    }),
    select: (data): { items: PolicyData[]; totalCount: number } => ({
      items: (data.context?.contents ?? []).map((policy) => ({
        id: policy.metaPolicyIdIdx ?? String(policy.id ?? ''),
        contentId: policy.id,
        title: policy.policyName ?? '',
        aiSummary: policy.aiSummary ?? EMPTY_AI_SUMMARY_MESSAGE,
        applicationPeriod: formatApplicationPeriod(
          policy.dueDateStart,
          policy.dueDateEnd,
        ),
        agencyName: policy.agency ?? '',
        agencyLogo: policy.agencyLogo ?? '',
        isBookmarked: Boolean(policy.wishlistYN),
      })),
      totalCount: data.context?.totalCount ?? 0,
    }),
  });

  const hometownNewsQuery = useQuery({
    ...UnauthQueryOptions['get:/wello/v2/allows/query/hometown-news']({
      query: {
        searchKeyword: keyword,
        hometownNewsSort: 'LATEST',
        page: 1,
        size: PREVIEW_SIZE,
      },
    }),
  });

  const bestContentsQuery = useQuery({
    ...UnauthQueryOptions['get:/wello/v2/allows/query/best-contents']({
      query: {
        searchKeyword: keyword,
        bestContentsSort: 'LATEST',
        page: 1,
        size: PREVIEW_SIZE,
      },
    }),
  });

  const giftQuery = useQuery({
    ...UnauthQueryOptions['get:/wello/v2/allows/query/gift-products']({
      query: {
        searchKeyword: keyword,
        page: 1,
        size: SLIDER_PREVIEW_SIZE,
      },
    }),
  });

  const donationBusinessQuery = useQuery({
    ...UnauthQueryOptions['get:/wello/v2/allows/query/donations-businesses']({
      query: {
        searchKeyword: keyword,
        page: 1,
        size: DONATION_PREVIEW_SIZE,
      },
    }),
  });

  const localMarketQuery = useQuery({
    ...UnauthQueryOptions['get:/wello/v2/allows/query/local-market']({
      query: {
        searchKeyword: keyword,
        page: 1,
        size: SLIDER_PREVIEW_SIZE,
      },
    }),
  });

  const policyItems = policyQuery.data?.items ?? [];
  const policyTotal = policyQuery.data?.totalCount ?? 0;
  const hometownNewsItems = hometownNewsQuery.data?.context?.contents ?? [];
  const bestContentsItems = bestContentsQuery.data?.context?.contents ?? [];
  const giftItems = giftQuery.data?.context?.contents ?? [];
  const donationItems = donationBusinessQuery.data?.context?.contents ?? [];
  const localMarketItems = localMarketQuery.data?.context?.contents ?? [];

  const isAllLoading =
    policyQuery.isPending &&
    hometownNewsQuery.isPending &&
    bestContentsQuery.isPending &&
    giftQuery.isPending &&
    donationBusinessQuery.isPending &&
    localMarketQuery.isPending;

  const isAllEmpty =
    !isAllLoading &&
    policyItems.length < 1 &&
    hometownNewsItems.length < 1 &&
    bestContentsItems.length < 1 &&
    giftItems.length < 1 &&
    donationItems.length < 1 &&
    localMarketItems.length < 1;

  return (
    <DesktopPageShell
      className="mt-20 w-full mobile-tablet:mt-0"
      rightAside={<DesktopSideAdBanner />}
    >
      {isAllEmpty ? (
        <SearchEmptyResults />
      ) : (
        <div className="flex flex-col gap-40 pb-40 mobile-tablet:pb-24">
          <PolicyPreviewSection
            keyword={keyword}
            items={policyItems}
            totalCount={policyTotal}
            isLoading={policyQuery.isPending}
          />
          <HometownNewsPreviewSection
            keyword={keyword}
            items={hometownNewsItems}
            totalCount={hometownNewsQuery.data?.context?.totalCount ?? 0}
            isLoading={hometownNewsQuery.isPending}
          />
          <BestContentsPreviewSection
            keyword={keyword}
            items={bestContentsItems}
            totalCount={bestContentsQuery.data?.context?.totalCount ?? 0}
            isLoading={bestContentsQuery.isPending}
          />
          <GiftPreviewSection
            keyword={keyword}
            items={giftItems}
            totalCount={giftQuery.data?.context?.totalCount ?? 0}
            isLoading={giftQuery.isPending}
          />
          <DonationBusinessPreviewSection
            keyword={keyword}
            items={donationItems}
            totalCount={donationBusinessQuery.data?.context?.totalCount ?? 0}
            isLoading={donationBusinessQuery.isPending}
          />
          <LocalMarketPreviewSection
            keyword={keyword}
            items={localMarketItems}
            totalCount={localMarketQuery.data?.context?.totalCount ?? 0}
            isLoading={localMarketQuery.isPending}
          />
        </div>
      )}
    </DesktopPageShell>
  );
}
